import { useState, useEffect, useRef, useCallback } from "react";
import PropTypes from "prop-types";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Typography,
  Box,
  TextField,
  Select,
  MenuItem,
  InputAdornment,
  Checkbox,
  FormControlLabel,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { placeholderText } from "../../constants/colors";
import { isValidEmail } from "../../utils/formatUtils";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import CommonSelect from "../common/CommonSelect";

const PREFERRED_CONTACT_METHODS = {
  PHONE: "Phone Number",
  EMAIL: "Email Address",
  ALT_PHONE: "Alternate Phone Number",
  ALT_EMAIL: "Alternate Email Address",
};

const Field = ({
  label,
  name,
  value,
  handleChange,
  disabled,
  type,
  options,
  placeholder,
  error,
  helperText,
  charLimit,
  sx = {},
}) => (
  <Box flex={1} minWidth={0}>
    <Typography
      sx={{
        fontWeight: 500,
        fontSize: "14px",
        lineHeight: "35px",
        color: "#403C6F",
      }}
    >
      {label}
    </Typography>
    {type === "select" ? (
      <Select
        fullWidth
        name={name}
        value={value}
        onChange={handleChange}
        displayEmpty
        size="small"
        sx={{
          height: "45px",
          background: "#fff",
          borderRadius: "8px",
          "& .MuiOutlinedInput-notchedOutline": { borderColor: "#E0E0E0" },
          ...(disabled
            ? {
                backgroundColor: "#EFEFEF",
                "&:hover .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#E0E0E0",
                },
              }
            : {}),
          ...sx,
        }}
        renderValue={(selected) => selected || `Select ${label}`}
      >
        {options.map((opt) => (
          <MenuItem key={opt} value={opt}>
            {opt}
          </MenuItem>
        ))}
      </Select>
    ) : (
      <TextField
        fullWidth
        name={name}
        value={value || ""}
        onChange={handleChange}
        placeholder={placeholder}
        size="small"
        disabled={disabled}
        error={error}
        helperText={helperText}
        slotProps={{
          input: charLimit
            ? {
                endAdornment: (
                  <InputAdornment position="end">
                    <Typography
                      component="span"
                      sx={{
                        fontSize: "12px",
                        color: "rgb(102, 102, 102)",
                        backgroundColor: "rgba(255, 255, 255, 0.9)",
                        mr: 0.5,
                      }}
                    >
                      {(value || "").length}/{charLimit}
                    </Typography>
                  </InputAdornment>
                ),
              }
            : undefined,
        }}
        sx={{
          "& .MuiOutlinedInput-root": {
            height: "45px",
            borderRadius: "8px",
            backgroundColor: "#fff",
            "& fieldset": {
              borderColor: error ? "#f44336" : "#E0E0E0",
            },
            "&:hover fieldset": {
              borderColor: error ? "#f44336" : "#BDBDBD",
            },
            "&.Mui-focused fieldset": {
              borderColor: error ? "#f44336" : "#3f51b5",
            },
          },
          ...(disabled
            ? {
                backgroundColor: "#EFEFEF",
                "& .MuiOutlinedInput-root": {
                  backgroundColor: "#EFEFEF",
                  "& fieldset": {
                    borderColor: "#E0E0E0",
                  },
                  "&:hover fieldset": {
                    borderColor: "#E0E0E0",
                  },
                },
              }
            : {}),
          ...(charLimit
            ? {
                "& .MuiOutlinedInput-input": {
                  pr: 0,
                },
              }
            : {
                "& .MuiOutlinedInput-input": {
                  paddingRight: "60px",
                },
              }),
          ...sx,
        }}
      />
    )}
  </Box>
);

Field.propTypes = {
  label: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  handleChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  type: PropTypes.string,
  options: PropTypes.array,
  placeholder: PropTypes.string,
  error: PropTypes.bool,
  helperText: PropTypes.string,
  charLimit: PropTypes.number,
  sx: PropTypes.object,
};

// Format address for display
const formatAddress = (addressObj) => {
  if (!addressObj) return "";
  if (typeof addressObj === "string") return addressObj;

  const addressParts = [
    addressObj.street || addressObj.line1,
    addressObj.line2,
    addressObj.city,
    addressObj.state,
    addressObj.postalCode || addressObj.zip || addressObj.pinCode,
  ];

  return addressParts.filter(Boolean).join(", ");
};

// Format allergies correctly whether it's an array or string
const formatAllergies = (allergyData) => {
  if (Array.isArray(allergyData)) {
    return allergyData.join(", ");
  }
  if (typeof allergyData === "string") {
    return allergyData;
  }
  return "";
};

const EditContactInfoModal = ({
  open,
  onClose,
  contactInfo = {},
  onSave,
  canWrite = true,
}) => {
  const getAvailableContactMethods = (info) => {
    const availableMethods = [];

    const primaryPhone = (info.phoneNumber || info.phone || "").trim();
    if (primaryPhone) {
      availableMethods.push(PREFERRED_CONTACT_METHODS.PHONE);
    }

    const primaryEmail = (info.email || "").trim();
    if (primaryEmail) {
      availableMethods.push(PREFERRED_CONTACT_METHODS.EMAIL);
    }

    const altPhone = (
      info.alternatePhoneNum ||
      info.alternatePhoneNumber ||
      ""
    ).trim();
    if (altPhone) {
      availableMethods.push(PREFERRED_CONTACT_METHODS.ALT_PHONE);
    }

    const altEmail = (
      info.alternateEmailAddress ||
      info.alternateEmail ||
      ""
    ).trim();
    if (altEmail) {
      availableMethods.push(PREFERRED_CONTACT_METHODS.ALT_EMAIL);
    }

    return availableMethods;
  };

  const determinePreferredContactMethod = (info) => {
    const availableMethods = getAvailableContactMethods(info);

    if (availableMethods.length === 0) return "Phone";

    return availableMethods[0];
  };

  const getPreferredContactMethod = (info) => {
    if (info.preferredContactMethod) {
      const availableMethods = getAvailableContactMethods(info);
      if (availableMethods.includes(info.preferredContactMethod)) {
        return info.preferredContactMethod;
      }
    }
    return determinePreferredContactMethod(info);
  };

  const [formData, setFormData] = useState(() => {
    return {
      phoneNumber: contactInfo.phoneNumber || contactInfo.phone || "",
      preferredContactMethod: getPreferredContactMethod(contactInfo),
      email: contactInfo.email || "",
      alternatePhoneNumber:
        contactInfo.alternatePhoneNum || contactInfo.alternatePhoneNumber || "",
      alternateEmail:
        contactInfo.alternateEmailAddress || contactInfo.alternateEmail || "",
      address: formatAddress(contactInfo.address || contactInfo) || "",
      allergies: formatAllergies(
        contactInfo.allergy || contactInfo.allergies || "",
      ),
      language: contactInfo.language || "",
      textingConsentObtained: contactInfo.textingConsentObtained || false,
    };
  });

  const [validationErrors, setValidationErrors] = useState({});
  const [isFormChanged, setIsFormChanged] = useState(false);
  const [initialFormData, setInitialFormData] = useState({});

  const resetForm = useCallback(() => {
    const formattedAddress = formatAddress(contactInfo.address || contactInfo);

    const initialData = {
      phoneNumber: contactInfo.phoneNumber || contactInfo.phone || "",
      preferredContactMethod: getPreferredContactMethod(contactInfo),
      email: contactInfo.email || "",
      alternatePhoneNumber:
        contactInfo.alternatePhoneNum || contactInfo.alternatePhoneNumber || "",
      alternateEmail:
        contactInfo.alternateEmailAddress || contactInfo.alternateEmail || "",
      address: formattedAddress,
      allergies: formatAllergies(
        contactInfo.allergy || contactInfo.allergies || "",
      ),
      language: contactInfo.language || "",
      timeZone: contactInfo.timeZone || "",
      textingConsentObtained: contactInfo.textingConsentObtained || false,
    };

    setFormData(initialData);
    setInitialFormData(initialData);
    setValidationErrors({});
    /* eslint-disable no-unused-vars */
    /* eslint-disable react-hooks/exhaustive-deps */
  }, [contactInfo]);

  useEffect(() => {
    if (open) {
      resetForm();
    }
  }, [open, contactInfo, resetForm]);

  const handleModalClose = () => {
    resetForm();
    onClose();
  };

  useEffect(() => {
    const hasChanges = Object.keys(formData).some((key) => {
      const currentValue = formData[key];
      const initialValue = initialFormData[key];

      const current =
        currentValue === null || currentValue === undefined
          ? ""
          : String(currentValue);
      const initial =
        initialValue === null || initialValue === undefined
          ? ""
          : String(initialValue);

      return current.trim() !== initial.trim();
    });

    setIsFormChanged(hasChanges);
  }, [formData, initialFormData, canWrite]);

  // Helper function to sanitize alternate phone number
  const sanitizeAlternatePhone = (name, value) => {
    if (name === "alternatePhoneNumber") {
      return String(value || "")
        .replaceAll(/\D/g, "")
        .slice(0, 10);
    }
    return value;
  };

  // Helper function to validate alternate phone number
  const validateAlternatePhone = (sanitizedAltPhone, newErrors) => {
    if (sanitizedAltPhone) {
      if (sanitizedAltPhone.length === 10) {
        delete newErrors.alternatePhoneNumber;
      } else {
        newErrors.alternatePhoneNumber =
          "Please enter a valid 10-digit phone number";
      }
    } else {
      delete newErrors.alternatePhoneNumber;
    }
  };

  // Helper function to determine preferred contact method (variant for form changes)
  const determinePreferredMethod = (
    newFormData,
    availableMethods,
    currentPreferred,
  ) => {
    // Prefer EMAIL if available, else fallback to PHONE, else ALT_EMAIL, else ALT_PHONE, else first available
    const priorityOrder = [
      PREFERRED_CONTACT_METHODS.EMAIL,
      PREFERRED_CONTACT_METHODS.PHONE,
      PREFERRED_CONTACT_METHODS.ALT_EMAIL,
      PREFERRED_CONTACT_METHODS.ALT_PHONE,
    ];
    if (!currentPreferred || !availableMethods.includes(currentPreferred)) {
      for (const method of priorityOrder) {
        if (availableMethods.includes(method)) {
          return method;
        }
      }
      return availableMethods[0] || currentPreferred || "Phone";
    }
    return currentPreferred;
  };

  // Helper function to prepare contact info for determination
  const prepareContactInfoForDetermination = (newFormData) => {
    return {
      ...newFormData,
      phone: newFormData.phoneNumber,
      alternatePhoneNum: newFormData.alternatePhoneNumber,
      alternateEmailAddress: newFormData.alternateEmail,
      allergy: newFormData.allergies,
    };
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    const sanitizedAltPhone = sanitizeAlternatePhone(name, value);

    const newFormData = {
      ...formData,
      [name]: sanitizedAltPhone,
    };

    const newErrors = { ...validationErrors };
    if (name === "alternatePhoneNumber") {
      validateAlternatePhone(sanitizedAltPhone, newErrors);
    }
    if (name === "alternateEmail") {
      const trimmed = (value || "").trim();
      if (trimmed) {
        if (isValidEmail(trimmed)) {
          delete newErrors.alternateEmail;
        } else {
          newErrors.alternateEmail = "Please enter a valid email address";
        }
      } else {
        delete newErrors.alternateEmail;
      }
    }

    const contactInfoForDetermination =
      prepareContactInfoForDetermination(newFormData);
    const availableMethods = getAvailableContactMethods(
      contactInfoForDetermination,
    );
    const currentPreferred = newFormData.preferredContactMethod;
    const preferredContactMethod = determinePreferredMethod(
      newFormData,
      availableMethods,
      currentPreferred,
    );

    setFormData({
      ...newFormData,
      preferredContactMethod,
    });
    setValidationErrors(newErrors);
    setIsFormChanged(true);
  };

  const handleSave = () => {
    const errors = {};

    if (formData.alternatePhoneNumber) {
      const rawAlt = formData.alternatePhoneNumber;
      const digitsOnlyAlt = rawAlt.replaceAll(/[^\d]/g, "");
      const hasAlphaAlt = /[A-Za-z]/.test(rawAlt);
      if (hasAlphaAlt || digitsOnlyAlt.length !== 10) {
        errors.alternatePhoneNumber =
          "Please enter a valid 10-digit phone number";
      }
    }

    if (formData.alternateEmail?.trim()) {
      if (!isValidEmail(formData.alternateEmail.trim())) {
        errors.alternateEmail = "Please enter a valid email address";
      }
    }

    const contactInfoForDetermination = {
      ...formData,
      phone: formData.phoneNumber,
      alternatePhoneNum: formData.alternatePhoneNumber,
      alternateEmailAddress: formData.alternateEmail,
      allergy: formData.allergies,
    };

    const availableMethods = getAvailableContactMethods(
      contactInfoForDetermination,
    );

    let preferredContactMethod = formData.preferredContactMethod;

    if (
      !preferredContactMethod ||
      !availableMethods.includes(preferredContactMethod)
    ) {
      preferredContactMethod = determinePreferredContactMethod(
        contactInfoForDetermination,
      );
    }

    const payload = {
      ...formData,
      preferredContactMethod,
      alternatePhoneNumber: (formData.alternatePhoneNumber || "")
        .replaceAll(/\D/g, "")
        .slice(0, 10),
    };

    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      return;
    }

    setValidationErrors({});
    onSave(payload);
  };

  const handleTypeToggle = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    setIsFormChanged(true);

    setAnchorEl({ ...anchorEl, [field]: null });
  };

  const filterButtonRefs = {
    preferredContactMethod: useRef(null),
  };

  const [anchorEl, setAnchorEl] = useState({
    preferredContactMethod: null,
  });

  const handleOpenSelect = (field) => (event) => {
    setAnchorEl({ ...anchorEl, [field]: event.currentTarget });
  };

  const handleCloseSelect = (field) => () => {
    setAnchorEl({ ...anchorEl, [field]: null });
  };

  return (
    <Dialog
      open={open}
      onClose={handleModalClose}
      maxWidth="sm"
      fullWidth={false}
      slotProps={{
        paper: {
          sx: {
            width: { xs: "95%", md: "900px" },
            maxWidth: "90vw",
            borderRadius: "16px",
            p: { xs: "16px", md: "24px" },
            backgroundColor: "#F7F8FB",
          },
        },
      }}
    >
      <DialogTitle
        sx={{
          p: 0,
          m: 0,
          width: "100%",
          height: "40px",
          display: "flex",
          flexDirection: "row",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography
          sx={{
            fontWeight: 700,
            fontSize: "20px",
            lineHeight: "30px",
            color: "#000000",
          }}
        >
          Edit Contact Information
        </Typography>
        <IconButton
          onClick={handleModalClose}
          aria-label="Close edit contact information dialog"
        >
          <CloseIcon sx={{ color: placeholderText }} />
        </IconButton>
      </DialogTitle>

      <DialogContent
        sx={{
          display: "flex",
          flexDirection: "column",
          width: { xs: "100%", md: "850px" },
          minHeight: "220px",
          borderRadius: "16px",
          border: "1px solid #E0E0E0",
          backgroundColor: "#FFFFFF",
          mt: 2,
          p: 2,
        }}
      >
        <Box
          display="flex"
          flexDirection={{ xs: "column", md: "row" }}
          gap={2}
          width="100%"
          mb={2}
        >
          <Box flex={1} minWidth={0}>
            <Field
              label="Phone Number"
              name="phoneNumber"
              value={formData.phoneNumber}
              handleChange={handleChange}
              placeholder="Enter phone number"
              fullWidth
              disabled={true}
              sx={{
                "& .MuiOutlinedInput-root.Mui-disabled": {
                  backgroundColor: "#EFEFEF",
                  "& fieldset": {
                    borderColor: "#E0E0E0",
                  },
                  "&:hover fieldset": {
                    borderColor: "#E0E0E0",
                  },
                },
              }}
            />

            <Field
              label="Alternate Phone Number"
              name="alternatePhoneNumber"
              value={formData.alternatePhoneNumber}
              handleChange={handleChange}
              placeholder="Enter alternate phone number"
              fullWidth
              disabled={false}
              error={!!validationErrors.alternatePhoneNumber}
              helperText={validationErrors.alternatePhoneNumber}
              charLimit={10}
              sx={{}}
            />
          </Box>

          <Box
            flex={1}
            minWidth={0}
            sx={{
              "& .MuiOutlinedInput-input": {
                whiteSpace: "normal",
                wordWrap: "break-word",
                overflowWrap: "break-word",
              },
            }}
          >
            <Field
              label="Email Address"
              name="email"
              value={formData.email}
              handleChange={handleChange}
              placeholder="Enter email"
              fullWidth
              disabled={true}
              sx={{
                "& .MuiOutlinedInput-root.Mui-disabled": {
                  backgroundColor: "#EFEFEF",
                  "& fieldset": {
                    borderColor: "#E0E0E0",
                  },
                  "&:hover fieldset": {
                    borderColor: "#E0E0E0",
                  },
                },
              }}
            />

            <Field
              label="Alternate Email Address"
              name="alternateEmail"
              value={formData.alternateEmail}
              handleChange={(e) => {
                // Prevent entering more than 75 characters
                if (e.target.value.length <= 74) {
                  handleChange(e);
                }
              }}
              placeholder="Enter alternate email address"
              fullWidth
              disabled={false}
              charLimit={74}
              sx={{}}
              inputProps={{ maxLength: 74 }}
              error={!!validationErrors.alternateEmail}
              helperText={validationErrors.alternateEmail}
            />
          </Box>
        </Box>

        <Box
          display="flex"
          flexDirection={{ xs: "column", md: "row" }}
          gap={2}
          width="100%"
          mb={2}
        >
          <Box flex={1} minWidth={0}>
            <Typography
              sx={{
                fontWeight: 500,
                fontSize: "14px",
                lineHeight: "35px",
                color: "#403C6F",
              }}
            >
              Preferred Contact Method
            </Typography>
            <Box>
              {(() => {
                const availableMethods = getAvailableContactMethods({
                  ...formData,
                  phone: formData.phoneNumber,
                  alternatePhoneNum: formData.alternatePhoneNumber,
                  alternateEmailAddress: formData.alternateEmail,
                  email: formData.email,
                });
                const disableDropdown = availableMethods.length === 0;
                return (
                  <Box
                    ref={filterButtonRefs.preferredContactMethod}
                    onClick={
                      !canWrite || disableDropdown
                        ? null
                        : handleOpenSelect("preferredContactMethod")
                    }
                    sx={{
                      width: "100%",
                      height: "45px",
                      border: "1px solid #E9E9E8",
                      borderRadius: "8px",
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      padding: "0 12px",
                      backgroundColor:
                        !canWrite || disableDropdown ? "#EFEFEF" : "#FFFFFF",
                      textTransform: "none",
                      color: (() => {
                        if (!canWrite || disableDropdown) {
                          return "#999893";
                        } else if (formData.preferredContactMethod) {
                          return "#000000";
                        } else {
                          return "#999893";
                        }
                      })(),
                      fontSize: "14px",
                      cursor:
                        !canWrite || disableDropdown ? "default" : "pointer",
                      "&:hover": {
                        backgroundColor:
                          !canWrite || disableDropdown ? "#F3F4F6" : "#FFFFFF",
                      },
                    }}
                  >
                    <span
                      style={{
                        fontWeight: formData.preferredContactMethod
                          ? "normal"
                          : "400",
                      }}
                    >
                      {formData.preferredContactMethod ||
                        "Select Preferred Contact Method"}
                    </span>
                    {!disableDropdown && (
                      <KeyboardArrowDownIcon
                        sx={{
                          color: "#6B7280",
                          fontSize: "20px",
                          marginLeft: "8px",
                        }}
                      />
                    )}
                  </Box>
                );
              })()}
              <CommonSelect
                id="preferred-contact-method-select"
                open={Boolean(anchorEl.preferredContactMethod)}
                anchorEl={anchorEl.preferredContactMethod}
                onClose={handleCloseSelect("preferredContactMethod")}
                inQuickview={false}
                filterButtonRef={filterButtonRefs.preferredContactMethod}
                noteTypes={getAvailableContactMethods({
                  ...formData,
                  phone: formData.phoneNumber,
                  alternatePhoneNum: formData.alternatePhoneNumber,
                  alternateEmailAddress: formData.alternateEmail,
                  email: formData.email,
                })}
                selectedTypes={
                  formData.preferredContactMethod
                    ? [formData.preferredContactMethod]
                    : []
                }
                handleTypeToggle={(type) => {
                  handleTypeToggle("preferredContactMethod", type);
                  handleCloseSelect("preferredContactMethod")();
                }}
                multiple={false}
                width={
                  filterButtonRefs.preferredContactMethod.current?.offsetWidth
                    ? `${filterButtonRefs.preferredContactMethod.current.offsetWidth}px`
                    : "auto"
                }
              />
            </Box>
          </Box>

          <Box sx={{ flex: 1, position: "relative" }}>
            <Field
              label="Language"
              name="language"
              value={formData.language}
              handleChange={(e) => {
                const value = e.target.value.replaceAll(/[^a-zA-Z\s]/g, "");

                const limitedValue = value.slice(0, 15);
                handleChange({
                  target: {
                    name: "language",
                    value: limitedValue,
                  },
                });
              }}
              placeholder="e.g. English"
              slotProps={{ htmlInput: { maxLength: 15 } }}
            />

            <Box
              sx={{
                position: "absolute",
                bottom: "8px",
                right: "8px",
                fontSize: "12px",

                backgroundColor: "rgba(255, 255, 255, 0.9)",
                padding: "2px 6px",
                borderRadius: "4px",
                pointerEvents: "none",
              }}
            >
              {formData.language?.length || 0}/15
            </Box>
          </Box>
        </Box>

        <Box width="100%" mb={2}>
          <Box sx={{ position: "relative" }}>
            <Field
              label="Known Allergies"
              name="allergies"
              value={formData.allergies}
              handleChange={(e) => {
                if (e.target.value.length <= 128) {
                  handleChange(e);
                }
              }}
              placeholder="e.g. Pollen, Peanuts"
              fullWidth
              error={validationErrors.allergies}
              helperText={
                validationErrors.allergies ? "Please enter a valid value" : ""
              }
              slotProps={{
                htmlInput: {
                  maxLength: 128,
                },
              }}
            />

            <Box
              sx={{
                position: "absolute",
                bottom: "8px",
                right: "8px",
                fontSize: "12px",
                backgroundColor: "rgba(255, 255, 255, 0.9)",
                padding: "2px 6px",
                borderRadius: "4px",
                pointerEvents: "none",
              }}
            >
              {String(formData.allergies || "").length}/128
            </Box>
          </Box>
        </Box>

        <Box width="100%" mb={2}>
          <Box sx={{ width: "100%" }}>
            <Typography
              sx={{
                fontWeight: 500,
                fontSize: "14px",
                lineHeight: "35px",
                color: "#403C6F",
              }}
            >
              Address
            </Typography>
            <Box
              sx={{
                width: "100%",
                minHeight: "100px",
                p: 2,
                borderRadius: "8px",
                backgroundColor: "#EFEFEF",
                border: "1px solid #E0E0E0",
                whiteSpace: "pre-line",
                lineHeight: "1.5",
                overflow: "auto",
                color: "#999893",
              }}
            >
              {typeof formData.address === "string" ? formData.address : "N/A"}
            </Box>
          </Box>
        </Box>
        <Box width="100%" mb={2} pl={1}>
          <Box sx={{ width: "100%" }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.textingConsentObtained || false}
                  onChange={(e) =>
                    handleChange({
                      target: {
                        name: "textingConsentObtained",
                        value: e.target.checked,
                      },
                    })
                  }
                  name="textingConsentObtained"
                  sx={{
                    color: "#9F9DB6",
                    "&.Mui-checked": {
                      color: "#403C6F !important",
                    },
                  }}
                  disabled={!canWrite}
                />
              }
              label={
                <Typography sx={{ fontSize: "13px", color: "#403C6F" }}>
                  Verbal consent obtained for informational/service text
                  messages; patient advised texts are unsecure and PHI will be
                  limited.
                </Typography>
              }
            />
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ justifyContent: "flex-end", gap: "10px", mt: 1 }}>
        <Button
          onClick={handleModalClose}
          variant="outlined"
          sx={{
            borderColor: "#E8E8E9",
            color: "#6E6D92",
            textTransform: "none",
            fontWeight: 500,
            px: 3,
            borderRadius: "52px",
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          color="primary"
          sx={{
            borderRadius: "52px",
            backgroundColor: "#403C6F",
            color: "#FFFFFF",
            textTransform: "none",
            fontSize: "14px",
            fontWeight: 600,
            px: 3,
            letterSpacing: 0,
            "&:hover": { backgroundColor: "#332F5B" },
            "&:focus-visible": {
              outline: "2px solid #1C6DD0",
              outlineOffset: "2px",
            },
            "&:disabled": {
              backgroundColor: "#D4D4D6",
              color: "#403C6F",
              cursor: "not-allowed",
              opacity: 1,
            },
          }}
          disabled={!isFormChanged || !canWrite}
          title={`isFormChanged: ${isFormChanged}, canWrite: ${canWrite}`}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

EditContactInfoModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  contactInfo: PropTypes.object,
  onSave: PropTypes.func.isRequired,
  canWrite: PropTypes.bool,
};

export default EditContactInfoModal;
